/**
 * A document's details, on the right, opened by clicking its row in a collection.
 *
 * NOT the citation reader (`DocumentReader.tsx`). That one is chunk-shaped: it needs a
 * citation and opens the passage an answer leaned on. This one starts from the DOCUMENT —
 * what it is, how it was filed, whether it is searchable yet — and offers the original
 * file. A reader looking at a collection wants to check the thing they just uploaded, and
 * had no way to.
 *
 * Every field is optional in the API because the corpus was ingested before the catalogue
 * backfill existed. A missing facet renders as "—", never as a default, so an unknown year
 * never reads as a real one.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Pill } from "@/components/primitives.tsx";
import { api } from "@/lib/api.ts";
import type { Collection, CorpusDocument } from "@/lib/api-types.ts";

/**
 * Which collection the drawer shows, given what the server last confirmed.
 *
 * The drawer is ONE component reused for every row, and the table behind it cannot see a
 * change until it refetches. So a just-filed value has to be held locally — but KEYED to
 * the document it belongs to. An unkeyed override follows the reader to the next document
 * and shows it filed somewhere it is not.
 */
export function shownCollection(
  document: { id: string; collection_id?: string | null } | null,
  justFiled: { id: string; collectionId: string | null } | null,
): string | null {
  if (document && justFiled && justFiled.id === document.id) return justFiled.collectionId;
  return document?.collection_id ?? null;
}

export function DocumentDrawer({
  document,
  collections = [],
  onChanged,
  onClose,
}: {
  document: CorpusDocument | null;
  /** Offered in the collection picker. Empty hides the picker entirely. */
  collections?: Collection[];
  /** The document's collection changed — the lists behind this drawer are now stale. */
  onChanged?: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const open = document !== null;
  const [showChunks, setShowChunks] = useState(false);

  // Optimistic, deliberately: the table behind this drawer cannot see the new value until
  // it refetches, and without this the select would snap back to the old collection for as
  // long as that takes — which reads as a failed save. See `shownCollection`.
  const [justFiled, setJustFiled] = useState<{ id: string; collectionId: string | null } | null>(
    null,
  );
  const currentCollection = shownCollection(document, justFiled);

  const file = useMutation({
    mutationFn: (collectionId: string | null) =>
      api.setDocumentCollection(document!.id, collectionId),
    onSuccess: (updated) => {
      setJustFiled({ id: updated.id, collectionId: updated.collection_id ?? null });
      onChanged?.();
    },
  });

  // Fetched only when asked for. A document can hold hundreds of passages and nobody
  // opening the drawer for a status wants to pay for them.
  const chunks = useQuery({
    queryKey: ["document", "chunks", document?.id],
    queryFn: () => api.documentChunks(document!.id, 20),
    enabled: open && showChunks && !!document,
  });

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="doc-drawer" aria-describedby={undefined}>
          {document ? (
            <>
              <div className="doc-drawer-head">
                <Dialog.Title className="doc-drawer-title">{document.title}</Dialog.Title>
                <Dialog.Close className="icon-btn" aria-label={t("common.close")}>
                  ×
                </Dialog.Close>
              </div>

              <div className="doc-drawer-body">
                <dl className="doc-facts">
                  <Fact label={t("library.columns.status")}>
                    <Pill
                      tone={
                        document.status === "indexed"
                          ? "green"
                          : document.status === "failed"
                            ? "red"
                            : "amber"
                      }
                    >
                      {t(`library.status.${document.status}`)}
                    </Pill>
                  </Fact>
                  {/* What a reader who just uploaded actually wants: is it searchable, and
                      how much of it. "Terindeks" alone does not say. */}
                  <Fact label={t("document.searchable")}>
                    {document.indexed_chunk_count > 0
                      ? t("document.chunkCount", { count: document.indexed_chunk_count })
                      : t("document.noChunks")}
                  </Fact>
                  {/* EDITABLE. The collection is the one fact about a document that a
                      reader routinely needs to correct, and until this existed it could
                      only ever be set by the upload that created the document. */}
                  {collections.length > 0 ? (
                    <Fact label={t("library.columns.collection")}>
                      <select
                        className="input"
                        value={currentCollection ?? ""}
                        disabled={file.isPending}
                        aria-label={t("library.columns.collection")}
                        onChange={(event) => file.mutate(event.target.value || null)}
                      >
                        <option value="">{t("library.uploadNone")}</option>
                        {collections.map((collection) => (
                          <option key={collection.id} value={collection.id}>
                            {collection.name}
                          </option>
                        ))}
                      </select>
                      {file.error ? (
                        <span className="hint" style={{ color: "var(--red)" }}>
                          {t("errors.generic")}
                        </span>
                      ) : null}
                    </Fact>
                  ) : null}
                  <Fact label={t("library.columns.format")}>{document.format ?? "—"}</Fact>
                  {/* Publication facts render ONLY when the document records them. Three
                      rows of "—" told the reader nothing and read as broken; an uploaded
                      file legitimately has none of them. */}
                  {document.year ? (
                    <Fact label={t("library.columns.year")}>{document.year}</Fact>
                  ) : null}
                  {document.wilayah ? (
                    <Fact label={t("library.columns.region")}>{document.wilayah}</Fact>
                  ) : null}
                  {document.jenis ? (
                    <Fact label={t("document.jenis")}>{document.jenis}</Fact>
                  ) : null}
                </dl>

                <div className="doc-drawer-actions">
                  <a
                    className="btn btn-primary"
                    href={api.documentContentUrl(document.id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("document.openOriginal")}
                  </a>
                  {document.source_url ? (
                    <a
                      className="btn btn-ghost"
                      href={document.source_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("document.openSource")}
                    </a>
                  ) : null}
                </div>

                {document.status !== "indexed" ? (
                  <p className="hint">{t("document.notIndexedYet")}</p>
                ) : null}

                {/* HOW IT WAS SPLIT. The count above says a document is searchable; this
                    shows what the system actually reads — the thing a reader asks for the
                    moment they stop trusting the number. */}
                {document.indexed_chunk_count > 0 ? (
                  <div className="doc-chunks">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowChunks((was) => !was)}
                    >
                      {showChunks ? t("document.hideChunks") : t("document.showChunks")}
                    </button>

                    {showChunks ? (
                      chunks.isPending ? (
                        <p className="hint">{t("common.loading")}</p>
                      ) : (
                        <>
                          <ol className="chunk-list">
                            {(chunks.data?.chunks ?? []).map((chunk, index) => (
                              <li key={chunk.chunk_id} className="chunk-item">
                                <div className="chunk-head mono">
                                  <span>#{index + 1 + (chunks.data?.offset ?? 0)}</span>
                                  {chunk.legal_reference ? (
                                    <span>{chunk.legal_reference}</span>
                                  ) : null}
                                  {chunk.page_number !== null ? (
                                    <span>{t("document.page", { page: chunk.page_number })}</span>
                                  ) : null}
                                </div>
                                <p className="chunk-text">{chunk.text}</p>
                              </li>
                            ))}
                          </ol>
                          {chunks.data && chunks.data.total > chunks.data.chunks.length ? (
                            <p className="hint">
                              {t("document.chunksShown", {
                                shown: chunks.data.chunks.length,
                                total: chunks.data.total,
                              })}
                            </p>
                          ) : null}
                        </>
                      )
                    ) : null}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="eyebrow">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
